import { useState, useEffect } from 'react';
import { Row, Col, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaKey, FaUserCircle } from 'react-icons/fa';
import futbolistaService from '../../services/futbolistaService';
import TutorLayout from '../../components/tutor/TutorLayout';

/**
 * Vista Detalle de Futbolista
 * 
 * Muestra información completa del futbolista:
 * - Datos personales
 * - Historial por temporadas
 * - Estadísticas ProPoints
 * - Información de clubes y divisiones
 */
const FutbolistaDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [futbolista, setFutbolista] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

  useEffect(() => {
    loadFutbolista();
  }, [id]);

  const loadFutbolista = async () => {
    try {
      setLoading(true);
      const response = await futbolistaService.getFutbolista(id);
      setFutbolista(response.data);
    } catch (error) {
      showAlert('Error al cargar futbolista', 'danger');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '-';
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-ES');
  };

  if (loading) {
    return (
      <TutorLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      </TutorLayout>
    );
  }

  if (!futbolista) {
    return (
      <TutorLayout>
        <Alert variant="danger">
          <h4>Futbolista no encontrado</h4>
          <p>El futbolista que buscas no existe o no tienes permisos para verlo.</p>
          <Button variant="outline-danger" onClick={() => navigate('/tutor/futbolistas')}>
            Volver a Mis Futbolistas
          </Button>
        </Alert>
      </TutorLayout>
    );
  }

  return (
    <TutorLayout>
      {/* Header con navegación */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <div className="me-4">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/tutor/futbolistas')}
              className="mb-2"
              size="sm"
            >
              <FaArrowLeft className="me-2" />
              Volver a Mis Futbolistas
            </Button>
            <div className="d-flex align-items-center">
              {/* Foto/Avatar junto al nombre */}
              <div className="me-3">
                {(() => {
                  // Verificar si tiene foto personal
                  const tienePersonal = futbolista.foto &&
                                      futbolista.foto.trim() !== '' &&
                                      !futbolista.foto.includes('default-player.jpg') &&
                                      !futbolista.foto.includes('default-avatar.jpg');

                  if (tienePersonal) {
                    return (
                      <img
                        src={futbolista.foto}
                        alt={futbolista.nombre}
                        className="rounded-circle"
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          border: '3px solid #28a745'
                        }}
                      />
                    );
                  } else {
                    return (
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          backgroundColor: '#f8f9fa',
                          border: '2px solid #6c757d',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FaUserCircle
                          style={{
                            fontSize: '50px',
                            color: '#6c757d'
                          }}
                        />
                      </div>
                    );
                  }
                })()}
              </div>
              <div>
                <h2 className="text-primary mb-1">
                  {futbolista.nombre}
                </h2>
                <p className="text-muted mb-0">Detalles completos del futbolista</p>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="warning"
            onClick={() => navigate(`/tutor/futbolistas/${id}/credenciales`)}
          >
            <FaKey className="me-2" />
            Credenciales
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/tutor/futbolistas/${id}/editar`)}
          >
            <FaEdit className="me-2" />
            Editar
          </Button>
        </div>
      </div>

      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({ show: false, message: '', variant: 'success' })} dismissible className="mb-4">
          {alert.message}
        </Alert>
      )}

      {/* Información Personal */}
      <div className="mb-4">
        <h5 className="text-secondary mb-3">Información Personal</h5>
        <div className="mb-3" style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '15px',
          border: '1px solid #e9ecef'
        }}>
          <Row className="align-items-center py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
            <Col sm={3}>
              <strong style={{ color: '#495057' }}>Fecha de Nacimiento:</strong>
            </Col>
            <Col sm={9}>
              <span style={{ color: '#6c757d' }}>{formatearFecha(futbolista.fecha_nacimiento) || '-'}</span>
            </Col>
          </Row>

          <Row className="align-items-center py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
            <Col sm={3}>
              <strong style={{ color: '#495057' }}>Edad:</strong>
            </Col>
            <Col sm={9}>
              <span style={{ color: '#6c757d' }}>{calcularEdad(futbolista.fecha_nacimiento)} años</span>
            </Col>
          </Row>

          <Row className="align-items-center py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
            <Col sm={3}>
              <strong style={{ color: '#495057' }}>Apodo:</strong>
            </Col>
            <Col sm={9}>
              <span style={{ color: '#6c757d' }}>{futbolista.apodo || '-'}</span>
            </Col>
          </Row>

          <Row className="align-items-center py-2">
            <Col sm={3}>
              <strong style={{ color: '#495057' }}>Año de Nacimiento:</strong>
            </Col>
            <Col sm={9}>
              <span style={{ color: '#6c757d' }}>{futbolista.ano_nacimiento || '-'}</span>
            </Col>
          </Row>
        </div>
      </div>

      {/* Historial de Temporadas */}
      {futbolista.temporadas && futbolista.temporadas.length > 0 && (
        <div style={{ marginTop: '3.5rem' }} className="mb-4">
          <h5 className="text-secondary mb-4">Historial de Temporadas</h5>
          <div className="table-responsive">
            <style>
              {`
                .table-spacious td, .table-spacious th {
                  padding: 12px 16px !important;
                  vertical-align: middle;
                }
                .table-spacious {
                  font-size: 0.95rem;
                }
              `}
            </style>
            <Table striped style={{
              minWidth: '900px',
              '--bs-table-striped-bg': 'rgba(0,0,0,0.02)'
            }} className="table-spacious">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '16%', minWidth: '130px' }}>Temporada</th>
                  <th style={{ width: '20%', minWidth: '160px' }}>Club</th>
                  <th style={{ width: '14%', minWidth: '110px' }}>División</th>
                  <th style={{ width: '18%', minWidth: '140px' }}>Posición</th>
                  <th style={{ width: '14%', minWidth: '110px' }}>ProPoints™</th>
                  <th style={{ width: '18%', minWidth: '140px' }}>Equipación</th>
                </tr>
              </thead>
              <tbody>
                {futbolista.temporadas.map((temporada, index) => (
                  <tr key={index}>
                    <td style={{ fontWeight: '600' }}>
                      {temporada.temporada?.nombre || '-'}
                    </td>
                    <td style={{ color: '#495057' }}>
                      {temporada.club?.nombre || '-'}
                    </td>
                    <td>
                      {(() => {
                        // Obtener texto de división de múltiples formas posibles
                        const divisionTexto = temporada.division?.competicion ||
                                            temporada.division?.nombre ||
                                            temporada.division?.division ||
                                            null;

                        if (divisionTexto && divisionTexto.trim() !== '') {
                          return (
                            <span
                              style={{
                                fontSize: '0.8rem',
                                padding: '4px 8px',
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                backgroundColor: '#6c757d',
                                color: '#ffffff',
                                border: '1px solid #6c757d',
                                borderRadius: '0.375rem',
                                fontWeight: '500',
                                lineHeight: '1.2',
                                textAlign: 'center',
                                verticalAlign: 'baseline',
                                minHeight: '20px'
                              }}
                            >
                              {divisionTexto}
                            </span>
                          );
                        } else {
                          return <span className="text-muted">Sin división</span>;
                        }
                      })()}
                    </td>
                    <td style={{ color: '#6c757d' }}>
                      <div style={{ lineHeight: '1.4' }}>
                        {temporada.demarcacion1?.posicion || '-'}
                        {temporada.demarcacion2 && (
                          <div style={{ fontSize: '0.85rem', color: '#8e9297' }}>
                            {temporada.demarcacion2.posicion}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{
                        backgroundColor: '#FFD700',
                        color: '#F57F17',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        border: '1px solid #F57F17',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        🌟 {temporada.puntos_propoint || 0} ProPoints™
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const tieneEquipacion = temporada.tiene_equipacion === true || temporada.tiene_equipacion === 'true';

                        // Versión simple con texto plano primero
                        if (tieneEquipacion) {
                          return (
                            <span style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              ✅ Sí tiene
                            </span>
                          );
                        } else {
                          return (
                            <span style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              ❌ No tiene
                            </span>
                          );
                        }
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* Información Adicional */}
      {futbolista.rrss && (
        <div className="mb-4">
          <h5 className="text-secondary mb-3">Información Adicional</h5>
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '15px',
            border: '1px solid #e9ecef'
          }}>
            <Row className="align-items-center">
              <Col sm={3}>
                <strong style={{ color: '#495057' }}>Redes Sociales:</strong>
              </Col>
              <Col sm={9}>
                <span style={{ color: '#6c757d' }}>{futbolista.rrss}</span>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </TutorLayout>
  );
};

export default FutbolistaDetail;
